// Safe wrappers around Web Storage.
//
// Any access to localStorage/sessionStorage — even reading `.length` or
// referencing `window.localStorage` — throws `SecurityError: The operation is
// insecure` when a browser blocks storage (Safari private browsing, disabled
// cookies/site-data, or embedded/third-party contexts). These wrappers guard
// every operation so callers degrade gracefully instead of crashing.

export interface SafeStorage {
  /** Returns the stored value, or null if missing or storage is unavailable. */
  getItem(key: string): string | null;
  /** Stores a value; a no-op if storage is unavailable. */
  setItem(key: string, value: string): void;
  /** Removes a value; a no-op if storage is unavailable. */
  removeItem(key: string): void;
  /** Returns all keys, or [] if storage is unavailable. */
  keys(): string[];
}

// Lazy getter: referencing `window.localStorage` itself can throw, so we defer
// access into the try/catch of each operation. Exported for tests.
export function createSafeStorage(getStorage: () => Storage): SafeStorage {
  return {
    getItem(key) {
      try {
        return getStorage().getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        getStorage().setItem(key, value);
      } catch {
        /* storage unavailable */
      }
    },
    removeItem(key) {
      try {
        getStorage().removeItem(key);
      } catch {
        /* storage unavailable */
      }
    },
    keys() {
      try {
        const storage = getStorage();
        const result: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i);
          if (key !== null) result.push(key);
        }
        return result;
      } catch {
        return [];
      }
    },
  };
}

// Reference the bare globals (not `window.*`): they are the same objects in a
// browser, but referencing the global directly also works in SSR/test contexts
// where a stub is placed on `globalThis` rather than `window`.
export const safeLocalStorage = createSafeStorage(() => localStorage);
export const safeSessionStorage = createSafeStorage(() => sessionStorage);
